import type { EventInput } from "@fullcalendar/core";
import type {
  BookingStatus,
  ProviderBooking,
  VenueOperatingHoursEntry,
} from "../../lib/api";

export const allBookingStatuses: BookingStatus[] = [
  "pending",
  "confirmed",
  "checked_in",
  "completed",
  "cancelled",
  "no_show",
  "rejected",
  "expired",
];

const statusEventPalette: Record<
  BookingStatus,
  { backgroundColor: string; borderColor: string; textColor: string }
> = {
  pending: {
    backgroundColor: "rgba(251, 191, 36, 0.18)",
    borderColor: "rgba(251, 191, 36, 0.5)",
    textColor: "#fef3c7",
  },
  confirmed: {
    backgroundColor: "rgba(52, 211, 153, 0.18)",
    borderColor: "rgba(52, 211, 153, 0.5)",
    textColor: "#d1fae5",
  },
  checked_in: {
    backgroundColor: "rgba(139, 92, 246, 0.18)",
    borderColor: "rgba(139, 92, 246, 0.5)",
    textColor: "#ede9fe",
  },
  // Terminal states keep only a faint hue echo on a muted slate text: the
  // calendar must read "done/dead" at a glance, not compete with the queue.
  completed: {
    backgroundColor: "rgba(96, 165, 250, 0.09)",
    borderColor: "rgba(96, 165, 250, 0.28)",
    textColor: "#94a3b8",
  },
  cancelled: {
    backgroundColor: "rgba(248, 113, 113, 0.09)",
    borderColor: "rgba(248, 113, 113, 0.28)",
    textColor: "#94a3b8",
  },
  no_show: {
    backgroundColor: "rgba(244, 114, 182, 0.09)",
    borderColor: "rgba(244, 114, 182, 0.28)",
    textColor: "#94a3b8",
  },
  rejected: {
    backgroundColor: "rgba(251, 146, 60, 0.09)",
    borderColor: "rgba(251, 146, 60, 0.28)",
    textColor: "#94a3b8",
  },
  expired: {
    backgroundColor: "rgba(148, 163, 184, 0.09)",
    borderColor: "rgba(148, 163, 184, 0.28)",
    textColor: "#94a3b8",
  },
};

// Light theme keeps the same hue per status but swaps pastel text for dark
// shades and strengthens the washes/borders so cards stay readable on white.
const statusEventPaletteLight: Record<
  BookingStatus,
  { backgroundColor: string; borderColor: string; textColor: string }
> = {
  pending: {
    backgroundColor: "rgba(251, 191, 36, 0.28)",
    borderColor: "rgba(217, 119, 6, 0.65)",
    textColor: "#78350f",
  },
  confirmed: {
    backgroundColor: "rgba(52, 211, 153, 0.26)",
    borderColor: "rgba(5, 150, 105, 0.6)",
    textColor: "#064e3b",
  },
  checked_in: {
    backgroundColor: "rgba(139, 92, 246, 0.24)",
    borderColor: "rgba(124, 58, 237, 0.6)",
    textColor: "#4c1d95",
  },
  completed: {
    backgroundColor: "rgba(96, 165, 250, 0.12)",
    borderColor: "rgba(96, 165, 250, 0.3)",
    textColor: "#64748b",
  },
  cancelled: {
    backgroundColor: "rgba(248, 113, 113, 0.12)",
    borderColor: "rgba(248, 113, 113, 0.3)",
    textColor: "#64748b",
  },
  no_show: {
    backgroundColor: "rgba(244, 114, 182, 0.12)",
    borderColor: "rgba(244, 114, 182, 0.3)",
    textColor: "#64748b",
  },
  rejected: {
    backgroundColor: "rgba(251, 146, 60, 0.12)",
    borderColor: "rgba(251, 146, 60, 0.3)",
    textColor: "#64748b",
  },
  expired: {
    backgroundColor: "rgba(148, 163, 184, 0.12)",
    borderColor: "rgba(148, 163, 184, 0.3)",
    textColor: "#64748b",
  },
};

export function getBookingStatusLabel(status: BookingStatus) {
  return status.replaceAll("_", " ");
}

/** The provider-facing contact is deliberately phone-only; email remains API/form data. */
export function getOperationalContact(phone: string | null | undefined, fallback: string) {
  return phone?.trim() || fallback;
}

export function escapeHtml(value: string) {
  return value.replace(/[&<>"']/g, (character) => {
    const entities: Record<string, string> = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    };
    return entities[character] ?? character;
  });
}

export function getBookingStatusTone(status: BookingStatus) {
  switch (status) {
    case "pending":
      return "border-amber-300/30 bg-amber-300/10 text-amber-100";
    case "confirmed":
      return "border-emerald-300/30 bg-emerald-300/10 text-emerald-100";
    case "checked_in":
      return "border-violet-300/30 bg-violet-300/10 text-violet-100";
    case "completed":
      return "border-sky-300/30 bg-sky-300/10 text-sky-100";
    case "cancelled":
      return "border-rose-400/30 bg-rose-400/10 text-rose-100";
    case "rejected":
      return "border-orange-400/30 bg-orange-400/10 text-orange-100";
    case "no_show":
      return "border-pink-400/30 bg-pink-400/10 text-pink-100";
    case "expired":
      return "border-slate-400/30 bg-slate-400/10 text-slate-300";
    default:
      return "border-white/10 bg-white/5 text-slate-200";
  }
}

/** Statuses that still need desk attention; everything else is history. */
export function isActionableBookingStatus(status: BookingStatus) {
  return status === "pending" || status === "confirmed" || status === "checked_in";
}

/**
 * The one status -> ops-chip tone map. Dashboard, list rows, and the side
 * panel all derive from this instead of inventing per-page mappings.
 */
export function getBookingStatusChipTone(status: BookingStatus) {
  switch (status) {
    case "pending":
      return "warning";
    case "confirmed":
      return "success";
    case "checked_in":
      return "info";
    case "cancelled":
    case "rejected":
    case "no_show":
      return "danger";
    default:
      return undefined;
  }
}

export function getAvailableStatusActions(status: BookingStatus) {
  switch (status) {
    case "pending":
      return [
        { label: "Confirm", nextStatus: "confirmed" as const, tone: "primary" as const },
        { label: "Decline", nextStatus: "rejected" as const, tone: "warning" as const },
        { label: "Cancel booking", nextStatus: "cancelled" as const, tone: "danger" as const },
      ];
    case "confirmed":
      return [
        { label: "Didn't show up", nextStatus: "no_show" as const, tone: "warning" as const },
        { label: "Cancel booking", nextStatus: "cancelled" as const, tone: "danger" as const },
      ];
    default:
      return [];
  }
}

function getZonedParts(date: Date, timeZone: string) {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });

  const parts = formatter.formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "";

  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour"),
    minute: get("minute"),
  };
}

export function formatDateTimeForPicker(value: string | Date, timeZone: string) {
  const date = typeof value === "string" ? new Date(value) : value;
  const parts = getZonedParts(date, timeZone);
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

export function formatCalendarRangeLabel(
  viewMode: "timeGridDay" | "timeGridWeek",
  currentDate: Date,
  timeZone: string,
  locale = "en-US",
  rangeSeparator = "to",
) {
  if (viewMode === "timeGridDay") {
    return new Intl.DateTimeFormat(locale, {
      timeZone,
      weekday: "long",
      month: "short",
      day: "numeric",
    }).format(currentDate);
  }

  const start = startOfWeek(currentDate);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const formatter = new Intl.DateTimeFormat(locale, {
    timeZone,
    month: "short",
    day: "numeric",
  });

  return `${formatter.format(start)} ${rangeSeparator} ${formatter.format(end)}`;
}

export function formatListRangeLabel(
  currentDate: Date,
  timeZone: string,
  locale = "en-US",
  rangeSeparator = "to",
) {
  const start = startOfWeek(currentDate);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const formatter = new Intl.DateTimeFormat(locale, {
    timeZone,
    month: "short",
    day: "numeric",
  });

  return `${formatter.format(start)} ${rangeSeparator} ${formatter.format(end)}`;
}

export function formatBookingSchedule(booking: ProviderBooking, locale = "en-US", rangeSeparator = "to") {
  const formatter = new Intl.DateTimeFormat(locale, {
    timeZone: booking.venue_timezone,
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

  return `${formatter.format(new Date(booking.scheduled_start_at))} ${rangeSeparator} ${formatter.format(
    new Date(booking.scheduled_end_at),
  )}`;
}

export function buildCalendarEvents(
  bookings: ProviderBooking[],
  selectedBookingId: string | null,
  theme: "dark" | "light" = "dark",
): EventInput[] {
  const statusPalette = theme === "light" ? statusEventPaletteLight : statusEventPalette;
  return bookings.map((booking) => {
    const palette = statusPalette[booking.status];

    return {
      id: booking.id,
      title: `${booking.unit_name} · ${booking.customer_name}`,
      start: booking.scheduled_start_at,
      end: booking.scheduled_end_at,
      editable: booking.status === "pending" || booking.status === "confirmed",
      backgroundColor: palette.backgroundColor,
      borderColor: palette.borderColor,
      textColor: palette.textColor,
      classNames: selectedBookingId === booking.id ? ["fc-booking-selected"] : [],
      extendedProps: {
        booking,
      },
    } satisfies EventInput;
  });
}

export function startOfWeek(date: Date) {
  const nextDate = new Date(date);
  const offset = nextDate.getDay();
  nextDate.setDate(nextDate.getDate() - offset);
  nextDate.setHours(0, 0, 0, 0);
  return nextDate;
}

const MINUTES_PER_DAY = 24 * 60;
// Matches the calendar's fixed slotMinTime (06:00); slotMaxTime must stay above it.
const CALENDAR_SLOT_MIN_MINUTES = 6 * 60;
// Previous fixed slotMaxTime, kept as the fallback when a venue has no usable hours.
const FALLBACK_SLOT_MAX_MINUTES = 26 * 60;
// One extra slot of breathing room below the latest close so bookings that end at
// close are not jammed against the bottom edge of the grid.
const SLOT_MAX_CLOSE_BUFFER_MINUTES = 30;
// Safety valve so a pathological schedule can never produce an unbounded grid.
const MAX_SLOT_MAX_MINUTES = 30 * 60;

function parseOperatingClockMinutes(value: string | null): number | null {
  if (!value) {
    return null;
  }

  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value.trim());
  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return null;
  }

  return hours * 60 + minutes;
}

/**
 * FullCalendar applies a single slotMaxTime to every day column, so the grid must
 * reach the latest close across the whole weekly schedule. Operating hours follow the
 * Sunday=0 day_of_week contract, but because we only need the view-wide maximum we
 * aggregate over every entry regardless of its day. Overnight windows (close earlier
 * than open, e.g. 11:00–03:30) belong to the day they opened, so their close lands
 * after midnight and is shifted by a full day.
 */
export function deriveSlotMaxMinutes(
  operatingHours: VenueOperatingHoursEntry[] | null | undefined,
): number {
  let latestClose: number | null = null;

  for (const entry of operatingHours ?? []) {
    if (!entry.is_open) {
      continue;
    }

    const openMinutes = parseOperatingClockMinutes(entry.open_time);
    const closeMinutes = parseOperatingClockMinutes(entry.close_time);
    if (openMinutes === null || closeMinutes === null) {
      continue;
    }

    const effectiveClose =
      closeMinutes < openMinutes ? closeMinutes + MINUTES_PER_DAY : closeMinutes;
    if (latestClose === null || effectiveClose > latestClose) {
      latestClose = effectiveClose;
    }
  }

  if (latestClose === null) {
    return FALLBACK_SLOT_MAX_MINUTES;
  }

  const buffered = latestClose + SLOT_MAX_CLOSE_BUFFER_MINUTES;
  return Math.min(
    MAX_SLOT_MAX_MINUTES,
    Math.max(CALENDAR_SLOT_MIN_MINUTES + SLOT_MAX_CLOSE_BUFFER_MINUTES, buffered),
  );
}

/** Format minutes-since-midnight as a FullCalendar duration ("hh:mm:ss"). */
export function formatSlotTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(remainingMinutes).padStart(2, "0")}:00`;
}

/**
 * Where the day and week grid starts: on the hour, an hour before the earliest
 * opening across the week (a 10:00 venue starts at 09:00). Venues without usable
 * hours keep the 06:00 default.
 */
export function deriveSlotMinMinutes(
  operatingHours: VenueOperatingHoursEntry[] | null | undefined,
): number {
  let earliestOpen: number | null = null;
  for (const entry of operatingHours ?? []) {
    if (!entry.is_open) continue;
    const openMinutes = parseOperatingClockMinutes(entry.open_time);
    const closeMinutes = parseOperatingClockMinutes(entry.close_time);
    if (openMinutes === null || closeMinutes === null) continue;
    earliestOpen = earliestOpen === null ? openMinutes : Math.min(earliestOpen, openMinutes);
  }
  if (earliestOpen === null) return CALENDAR_SLOT_MIN_MINUTES;
  return Math.max(0, Math.floor((earliestOpen - 60) / 60) * 60);
}

/** "Sep 26 to Oct 2" from the calendar's real visible range (end is exclusive). */
export function formatVisibleRangeLabel(
  start: Date,
  endExclusive: Date,
  timeZone: string,
  locale = "en-US",
  rangeSeparator = "to",
) {
  const formatter = new Intl.DateTimeFormat(locale, { timeZone, month: "short", day: "numeric" });
  const lastDay = new Date(endExclusive.getTime() - 1);
  const first = formatter.format(start);
  const last = formatter.format(lastDay);
  return first === last
    ? new Intl.DateTimeFormat(locale, { timeZone, weekday: "long", month: "short", day: "numeric" }).format(start)
    : `${first} ${rangeSeparator} ${last}`;
}
